import React, { useState, useEffect } from 'react';
import AdminSidebar from '../../components/admin/AdminSidebar';
import Logo from '../../components/common/Logo';
import { format, differenceInDays } from 'date-fns';
import { it } from 'date-fns/locale';
import { Search, Download, Loader, CreditCard, FileSpreadsheet } from 'lucide-react';
import PaymentService from '../../services/payment.service';
import { getStatusLabel, getStatusColor } from '../../utils/appointmentUtils';
import * as XLSX from 'xlsx';

const AdminPaymentsPage = () => {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [showExportOptions, setShowExportOptions] = useState(false);

  useEffect(() => {
    loadPayments();
  }, []);

  const loadPayments = async () => {
    try {
      setLoading(true);
      const data = await PaymentService.getAllPayments();
      setPayments(data);
      setError(null);
    } catch (err) {
      console.error('Error loading payments:', err);
      setError('Impossibile caricare i pagamenti');
    } finally {
      setLoading(false);
    }
  };

  // Funzione helper per calcolare i giorni rimanenti per un appuntamento
  const getDaysRemaining = (appointmentDate) => {
    if (!appointmentDate) return 0;
    const today = new Date();
    const apptDate = new Date(appointmentDate);
    return differenceInDays(apptDate, today);
  };

  // Funzione per ottenere l'etichetta dello stato dell'appuntamento
  const getAppointmentStatusLabel = (appointment) => {
    if (!appointment) return 'N/A';
    const daysRemaining = getDaysRemaining(appointment.date);
    return getStatusLabel(appointment.status, daysRemaining);
  };

  // Funzione per ottenere la classe CSS dello stato dell'appuntamento
  const getAppointmentStatusColor = (appointment) => {
    if (!appointment) return 'bg-gray-100 text-gray-800';
    const daysRemaining = getDaysRemaining(appointment.date);
    return getStatusColor(appointment.status, daysRemaining);
  };

  const filteredPayments = payments.filter(payment => {
    const matchesSearch = 
      (payment.user?.first_name + ' ' + payment.user?.last_name).toLowerCase().includes(searchTerm.toLowerCase()) ||
      payment.appointment?.service_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      payment.amount.toString().includes(searchTerm);
    
    const matchesFilter = filterStatus === 'all' || payment.appointment?.status === filterStatus;
    
    return matchesSearch && matchesFilter;
  });

  const totalAmount = filteredPayments
    .filter(payment => payment.status === 'completed')
    .reduce((sum, payment) => sum + parseFloat(payment.amount), 0)
    .toFixed(2);

  const exportToCSV = () => {
    const headers = ['Data', 'Paziente', 'Professionista', 'Servizio', 'Importo', 'Stato Appuntamento', 'Stato Pagamento'];
    
    const csvRows = [
      headers.join(','),
      ...filteredPayments.map(payment => {
        return [
          format(new Date(payment.created_at), 'dd/MM/yyyy', { locale: it }),
          `${payment.user?.first_name} ${payment.user?.last_name}`,
          `${payment.appointment?.therapist?.user?.first_name} ${payment.appointment?.therapist?.user?.last_name}`,
          payment.appointment?.service_name,
          payment.amount,
          getAppointmentStatusLabel(payment.appointment),
          payment.status === 'completed' ? 'Completato' : payment.status === 'pending' ? 'In attesa' : 'Fallito'
        ].join(',');
      })
    ];
    
    const csvContent = "data:text/csv;charset=utf-8," + csvRows.join('\n');
    const encodedUri = encodeURI(csvContent);
    
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `pagamenti_${format(new Date(), 'yyyyMMdd')}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setShowExportOptions(false);
  };

  const exportToExcel = () => {
    const headers = ['Data', 'Paziente', 'Professionista', 'Servizio', 'Importo', 'Stato Appuntamento', 'Stato Pagamento'];
    
    const data = [
      headers,
      ...filteredPayments.map(payment => [
        format(new Date(payment.created_at), 'dd/MM/yyyy HH:mm', { locale: it }),
        `${payment.user?.first_name} ${payment.user?.last_name}`,
        `${payment.appointment?.therapist?.user?.first_name} ${payment.appointment?.therapist?.user?.last_name}`,
        payment.appointment?.service_name,
        payment.amount,
        getAppointmentStatusLabel(payment.appointment),
        payment.status === 'completed' ? 'Completato' : payment.status === 'pending' ? 'In attesa' : 'Fallito'
      ])
    ];
    
    // Crea un nuovo workbook
    const wb = XLSX.utils.book_new();
    
    // Crea un worksheet dai dati
    const ws = XLSX.utils.aoa_to_sheet(data);
    
    // Aggiungi il worksheet al workbook
    XLSX.utils.book_append_sheet(wb, ws, 'Pagamenti');
    
    // Configura larghezze colonne
    const colWidths = [
      { wch: 16 },  // Data
      { wch: 20 },  // Paziente
      { wch: 20 },  // Professionista
      { wch: 25 },  // Servizio
      { wch: 10 },  // Importo
      { wch: 18 },  // Stato Appuntamento
      { wch: 15 },  // Stato Pagamento
    ];
    ws['!cols'] = colWidths;
    
    // Genera il file e avvia il download
    XLSX.writeFile(wb, `pagamenti_${format(new Date(), 'yyyyMMdd')}.xlsx`);
    setShowExportOptions(false);
  };

  // Calcola le statistiche per gli stati degli appuntamenti
  const appointmentStats = {
    booked: filteredPayments.filter(p => p.appointment?.status === 'booked').length,
    confirmed: filteredPayments.filter(p => p.appointment?.status === 'confirmed').length,
    suggested: filteredPayments.filter(p => p.appointment?.status === 'suggested').length,
    cancelled: filteredPayments.filter(p => p.appointment?.status === 'cancelled').length,
  };

  return (
    <div className="flex min-h-screen">
      <div className="w-64 bg-white shadow-md">
        <div className="p-4">
          <Logo />
        </div>
        <AdminSidebar />
      </div>

      <div className="flex-1 p-8 bg-gray-50">
        <header className="mb-6">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-bold">Gestione Pagamenti</h1>
              <p className="text-gray-600">Monitoraggio di tutti i pagamenti della piattaforma</p>
            </div>
            <div className="relative">
              <button 
                onClick={() => setShowExportOptions(!showExportOptions)}
                className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 flex items-center"
              >
                <Download className="w-4 h-4 mr-2" />
                Esporta
              </button>
              
              {showExportOptions && (
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg z-10">
                  <div className="py-1">
                    <button
                      onClick={exportToCSV}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Esporta CSV
                    </button>
                    <button
                      onClick={exportToExcel}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                    >
                      <FileSpreadsheet className="w-4 h-4 mr-2" />
                      Esporta Excel
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
          
          <div className="mt-4 flex flex-wrap gap-4">
            <div className="relative flex-grow">
              <input
                type="text"
                placeholder="Cerca per nome, servizio o importo..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full p-3 pl-10 border rounded-lg"
              />
              <Search className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            </div>
            
            <select 
              value={filterStatus} 
              onChange={(e) => setFilterStatus(e.target.value)}
              className="p-3 border rounded-lg min-w-40"
            >
              <option value="all">Tutti gli stati</option>
              <option value="booked">Da pagare</option>
              <option value="confirmed">Confermata/Svolta</option>
              <option value="suggested">Suggerita</option>
              <option value="cancelled">Annullata</option>
            </select>
          </div>
        </header>

        <div className="mb-6 grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="bg-white p-6 rounded-lg shadow">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-yellow-100 mr-4">
                <CreditCard className="h-6 w-6 text-yellow-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Da pagare</p>
                <p className="text-xl font-semibold">{appointmentStats.booked}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white p-6 rounded-lg shadow">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-green-100 mr-4">
                <CreditCard className="h-6 w-6 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Confermata/Svolta</p>
                <p className="text-xl font-semibold">{appointmentStats.confirmed}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white p-6 rounded-lg shadow">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-purple-100 mr-4">
                <CreditCard className="h-6 w-6 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Suggerita</p>
                <p className="text-xl font-semibold">{appointmentStats.suggested}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white p-6 rounded-lg shadow">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-gray-100 mr-4">
                <CreditCard className="h-6 w-6 text-gray-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Annullata</p>
                <p className="text-xl font-semibold">{appointmentStats.cancelled}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="mb-4 bg-white p-4 rounded-lg shadow">
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-600">Incasso totale (pagamenti completati):</span>
            <span className="text-lg font-semibold text-green-600">€{totalAmount}</span>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center items-center h-64">
            <Loader className="w-8 h-8 animate-spin text-purple-600" />
          </div>
        ) : error ? (
          <div className="bg-red-50 p-4 rounded-md text-red-700">
            {error}
          </div>
        ) : filteredPayments.length === 0 ? (
          <div className="bg-white p-8 rounded-lg shadow text-center">
            <CreditCard className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Nessun pagamento trovato
            </h3>
            <p className="text-gray-500">
              {searchTerm || filterStatus !== 'all' 
                ? "Nessun pagamento corrisponde ai criteri di ricerca"
                : "Non ci sono ancora pagamenti registrati nel sistema"}
            </p>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Data
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Paziente
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Professionista
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Servizio
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Importo
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Stato Appuntamento
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Stato Pagamento
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredPayments.map((payment) => (
                    <tr key={payment.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        {format(new Date(payment.created_at), 'dd/MM/yyyy HH:mm', { locale: it })}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {payment.user?.first_name} {payment.user?.last_name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {payment.appointment?.therapist?.user?.first_name} {payment.appointment?.therapist?.user?.last_name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {payment.appointment?.service_name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap font-medium">
                        €{payment.amount}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getAppointmentStatusColor(payment.appointment)}`}>
                          {getAppointmentStatusLabel(payment.appointment)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          payment.status === 'completed' ? 'bg-green-100 text-green-800' :
                          payment.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-red-100 text-red-800'
                        }`}>
                          {payment.status === 'completed' ? 'Completato' :
                           payment.status === 'pending' ? 'In attesa' : 'Fallito'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPaymentsPage;